import { openDB } from 'idb';

const DB_NAME = 'sigap-guru-db';
const DB_VERSION = 3; // Upgraded for foto kegiatan offline queue

// Initialize IndexedDB
export const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      // Store for pending attendance (offline queue)
      if (!db.objectStoreNames.contains('pendingAttendance')) {
        const store = db.createObjectStore('pendingAttendance', {
          keyPath: 'id',
          autoIncrement: true
        });
        store.createIndex('timestamp', 'timestamp');
        store.createIndex('synced', 'synced');
        store.createIndex('tipe', 'tipe');
      } else if (oldVersion < 2) {
        const store = transaction.objectStore('pendingAttendance');
        if (!store.indexNames.contains('tipe')) {
          store.createIndex('tipe', 'tipe');
        }
      }

      // Store for cached user data
      if (!db.objectStoreNames.contains('userData')) {
        db.createObjectStore('userData', { keyPath: 'key' });
      }

      // Store for cached school data
      if (!db.objectStoreNames.contains('schoolData')) {
        db.createObjectStore('schoolData', { keyPath: 'id' });
      }

      // Store for attendance history cache
      if (!db.objectStoreNames.contains('attendanceHistory')) {
        const historyStore = db.createObjectStore('attendanceHistory', {
          keyPath: 'id'
        });
        historyStore.createIndex('date', 'date');
        historyStore.createIndex('tipe', 'tipe');
        historyStore.createIndex('waktu_absen', 'waktu_absen');
      }

      // Store for pending foto kegiatan (offline queue) - v3
      if (!db.objectStoreNames.contains('pendingFotoKegiatan')) {
        const fotoStore = db.createObjectStore('pendingFotoKegiatan', {
          keyPath: 'id',
          autoIncrement: true
        });
        fotoStore.createIndex('timestamp', 'timestamp');
        fotoStore.createIndex('synced', 'synced');
      }
    }
  });
};

// Attendance Queue Operations
export const addToAttendanceQueue = async (attendanceData) => {
  const db = await initDB();
  const id = Date.now();
  const record = {
    id,
    ...attendanceData,
    timestamp: new Date().toISOString(),
    synced: false
  };
  await db.add('pendingAttendance', record);
  return record;
};

export const getPendingAttendance = async () => {
  const db = await initDB();
  const all = await db.getAll('pendingAttendance');
  return all.filter(record => record.synced === false);
};

export const markAttendanceSynced = async (id) => {
  const db = await initDB();
  const record = await db.get('pendingAttendance', id);
  if (record) {
    record.synced = true;
    await db.put('pendingAttendance', record);
  }
};

export const deletePendingAttendance = async (id) => {
  const db = await initDB();
  await db.delete('pendingAttendance', id);
};

export const getPendingCount = async () => {
  const db = await initDB();
  const all = await db.getAll('pendingAttendance');
  const pending = all.filter(record => record.synced === false);
  return pending.length;
};

// User Data Cache
export const cacheUserData = async (userData) => {
  const db = await initDB();
  await db.put('userData', { key: 'currentUser', ...userData });
};

export const getCachedUserData = async () => {
  const db = await initDB();
  return db.get('userData', 'currentUser');
};

export const clearUserData = async () => {
  const db = await initDB();
  await db.delete('userData', 'currentUser');
};

// School Data Cache
export const cacheSchoolData = async (schoolData) => {
  const db = await initDB();
  await db.put('schoolData', schoolData);
};

export const getCachedSchoolData = async (schoolId) => {
  const db = await initDB();
  return db.get('schoolData', schoolId);
};

// Token Management
export const cacheToken = async (token) => {
  const db = await initDB();
  await db.put('userData', { key: 'token', token });
};

export const getCachedToken = async () => {
  const db = await initDB();
  const data = await db.get('userData', 'token');
  return data?.token;
};

export const clearToken = async () => {
  const db = await initDB();
  await db.delete('userData', 'token');
};

// Attendance History Cache
export const cacheAttendanceHistory = async (attendanceList) => {
  const db = await initDB();
  const tx = db.transaction('attendanceHistory', 'readwrite');
  for (const attendance of attendanceList) {
    await tx.store.put(attendance);
  }
  await tx.done;
};

export const getCachedAttendanceHistory = async () => {
  const db = await initDB();
  return db.getAll('attendanceHistory');
};

/**
 * Get today's attendance status from offline data (IndexedDB)
 * Combines cached attendance history + pending offline attendance
 * @returns {Object} - { has_masuk, has_pulang, masuk_time, pulang_time }
 */
export const getOfflineTodayStatus = async () => {
  const db = await initDB();
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  
  // Check cached attendance history (synced data)
  const cachedHistory = await db.getAll('attendanceHistory');
  const todayCached = cachedHistory.filter(a => 
    a.waktu_absen && a.waktu_absen.startsWith(today)
  );
  
  // Check pending attendance (not yet synced)
  const pendingAll = await db.getAll('pendingAttendance');
  const todayPending = pendingAll.filter(a => {
    const timestamp = a.timestamp || a.offline_timestamp || a.waktu_absen;
    return timestamp && timestamp.startsWith(today) && !a.synced;
  });
  
  // Combine both sources
  const allTodayAttendance = [...todayCached, ...todayPending];
  
  const masukRecords = allTodayAttendance.filter(a => a.tipe === 'masuk');
  const pulangRecords = allTodayAttendance.filter(a => a.tipe === 'pulang');
  
  return {
    has_masuk: masukRecords.length > 0,
    has_pulang: pulangRecords.length > 0,
    masuk_time: masukRecords[0]?.waktu_absen || masukRecords[0]?.timestamp,
    pulang_time: pulangRecords[0]?.waktu_absen || pulangRecords[0]?.timestamp,
    message: masukRecords.length > 0 && pulangRecords.length > 0
      ? 'Anda sudah absen masuk dan pulang hari ini (data offline)'
      : masukRecords.length > 0
        ? 'Anda sudah absen masuk (data offline). Silakan absen pulang.'
        : 'Belum ada absen hari ini',
    is_offline_data: true
  };
};

/**
 * Cache today's attendance when fetched from server
 * This ensures offline mode can read the data
 */
export const cacheTodayAttendance = async (attendanceData) => {
  if (!attendanceData) return;
  
  const db = await initDB();
  const today = new Date().toISOString().split('T')[0];
  
  // If it's an array, cache all
  if (Array.isArray(attendanceData)) {
    const todayRecords = attendanceData.filter(a => 
      a.waktu_absen && a.waktu_absen.startsWith(today)
    );
    for (const record of todayRecords) {
      await db.put('attendanceHistory', { ...record, cached_at: new Date().toISOString() });
    }
  } else if (attendanceData.id) {
    // Single record
    await db.put('attendanceHistory', { ...attendanceData, cached_at: new Date().toISOString() });
  }
};

// Clear all data (logout)
export const clearAllData = async () => {
  const db = await initDB();
  await db.clear('userData');
  await db.clear('attendanceHistory');
  // Keep pending attendance for sync
};

// ================= FACE DESCRIPTOR CACHE (for Offline Face Verification) =================

/**
 * Cache face descriptor for offline face verification
 * @param {string} userId - User ID
 * @param {number[]} faceDescriptor - 128-dimension face descriptor array
 */
export const cacheFaceDescriptor = async (userId, faceDescriptor) => {
  if (!faceDescriptor || faceDescriptor.length !== 128) {
    console.warn('Invalid face descriptor - must be 128 dimensions');
    return false;
  }
  
  const db = await initDB();
  await db.put('userData', { 
    key: 'faceDescriptor', 
    userId,
    descriptor: faceDescriptor,
    cachedAt: new Date().toISOString()
  });
  console.log('Face descriptor cached for offline verification');
  return true;
};

/**
 * Get cached face descriptor for offline verification
 * @returns {Object|null} - { userId, descriptor, cachedAt } or null
 */
export const getCachedFaceDescriptor = async () => {
  const db = await initDB();
  const data = await db.get('userData', 'faceDescriptor');
  return data || null;
};

/**
 * Clear cached face descriptor (on logout or re-registration)
 */
export const clearCachedFaceDescriptor = async () => {
  const db = await initDB();
  await db.delete('userData', 'faceDescriptor');
};

/**
 * Update offline attendance record with face verification flag
 * @param {number} recordId - Pending attendance record ID
 * @param {boolean} faceVerified - Whether face was verified offline
 * @param {number} matchScore - Face match score (0-100)
 */
export const updateOfflineAttendanceFaceStatus = async (recordId, faceVerified, matchScore = null) => {
  const db = await initDB();
  const record = await db.get('pendingAttendance', recordId);
  if (record) {
    record.face_verified_offline = faceVerified;
    record.face_match_score_offline = matchScore;
    record.face_verified_at = new Date().toISOString();
    await db.put('pendingAttendance', record);
  }
};

// ========== Foto Kegiatan Offline Queue ==========

/**
 * Add foto kegiatan to offline queue for later sync.
 */
export const addToFotoKegiatanQueue = async (fotoData) => {
  const db = await initDB();
  const id = Date.now();
  const record = {
    id,
    foto_base64: fotoData.foto_base64,
    keterangan: fotoData.keterangan || '',
    timestamp: new Date().toISOString(),
    synced: false,
  };
  await db.add('pendingFotoKegiatan', record);
  return record;
};

/**
 * Get all pending (unsynced) foto kegiatan.
 */
export const getPendingFotoKegiatan = async () => {
  const db = await initDB();
  const all = await db.getAll('pendingFotoKegiatan');
  return all.filter(r => r.synced === false);
};

/**
 * Mark a foto kegiatan as synced.
 */
export const markFotoKegiatanSynced = async (id) => {
  const db = await initDB();
  const record = await db.get('pendingFotoKegiatan', id);
  if (record) {
    record.synced = true;
    record.synced_at = new Date().toISOString();
    await db.put('pendingFotoKegiatan', record);
  }
};

/**
 * Delete a pending foto kegiatan from queue.
 */
export const deletePendingFotoKegiatan = async (id) => {
  const db = await initDB();
  await db.delete('pendingFotoKegiatan', id);
};

/**
 * Get count of pending foto kegiatan.
 */
export const getPendingFotoKegiatanCount = async () => {
  const db = await initDB();
  const all = await db.getAll('pendingFotoKegiatan');
  return all.filter(r => r.synced === false).length;
};

